<template>
  <div class="home">
    <div class="home-container">
      <div
        class="page-header"
        style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
        "
      >
        <div
          style="
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
            flex: 1;
          "
        >
          <PageHeader
            :title="t('home.dashboard', settings.language)"
            :sidebarVisible="sidebarVisible"
            @hide-sidebar="$emit('hide-sidebar', $event)"
          />
          <p v-if="!loading && !error">
            {{ t("home.welcome", settings.language) }}
            <span v-if="companies.length > 0" class="company-count">
              • {{ companies.length }}
              {{ t("home.companiesInDatabase", settings.language) }}
            </span>
          </p>
          <p v-else-if="loading">{{ t("home.welcome", settings.language) }}</p>
          <p v-else>{{ t("home.welcome", settings.language) }}</p>
        </div>
        <AddFabMenu @add="onAddFabMenu" />
      </div>

      <div class="tools-grid">
        <!-- Agenda tile -->
        <router-link v-if="canViewAgenda" to="/agenda" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/time.svg"
                :alt="t('home.agenda.title', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.agenda.title", settings.language) }}</h3>
            <p>{{ t("home.agenda.description", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Companies tile -->
        <router-link v-if="canViewCompanies" to="/companies" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/group.svg"
                alt="Bedrijven"
                class="tool-icon-img"
              />
            </div>
            <h3>Bedrijven</h3>
            <p>Bekijk en beheer alle bedrijfsentiteiten.</p>
          </div>
        </router-link>

        <router-link v-if="canViewContacts" to="/contacts" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/group.svg"
                alt="Contacten"
                class="tool-icon-img"
              />
            </div>
            <h3>Contacten</h3>
            <p>Bekijk en beheer alle contacten.</p>
          </div>
        </router-link>

        <!-- Projects tile -->
        <router-link v-if="canViewProjects" to="/projects" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/list.svg"
                :alt="t('home.projects', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.projects", settings.language) }}</h3>
            <p>{{ t("home.projectsDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- WhereAbouts tile -->
        <router-link v-if="canViewProjects" to="/whereabouts" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/stats.svg"
                :alt="t('home.whereabouts', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.whereabouts", settings.language) }}</h3>
            <p>{{ t("home.whereaboutsDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Artikelen tile -->
        <router-link v-if="canViewArticles" to="/articles" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/list.svg"
                alt="Artikelen"
                class="tool-icon-img"
              />
            </div>
            <h3>Artikelen</h3>
            <p>Bekijk en beheer alle artikelen.</p>
          </div>
        </router-link>

        <!-- Prospecting tile -->
        <router-link
          v-if="canViewProspectie"
          to="/prospectie"
          class="tool-card"
        >
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/search.svg"
                :alt="t('home.prospecting', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.prospecting", settings.language) }}</h3>
            <p>{{ t("home.prospectingDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Influencers tile -->
        <router-link
          v-if="canViewInfluencers"
          to="/influencers"
          class="tool-card"
        >
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/group.svg"
                :alt="t('home.influencers', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.influencers", settings.language) }}</h3>
            <p>{{ t("home.influencersDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Artists tile -->
        <router-link
          v-if="canViewArtists"
          to="/artists"
          class="tool-card"
        >
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/group.svg"
                :alt="t('home.artists', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.artists", settings.language) }}</h3>
            <p>{{ t("home.artistsDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Email Templates tile -->
        <router-link
          v-if="canViewEmailTemplates"
          to="/email-templates"
          class="tool-card"
        >
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/email.svg"
                :alt="t('home.emailTemplates', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.emailTemplates", settings.language) }}</h3>
            <p>{{ t("home.emailTemplatesDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Offertes tile -->
        <router-link v-if="canViewOffertes" to="/offertes" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/list.svg"
                :alt="t('home.offertes', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.offertes", settings.language) }}</h3>
            <p>{{ t("home.offertesDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Deals tile -->
        <router-link v-if="canViewDeals" to="/deals" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/list.svg"
                :alt="t('home.deals', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.deals", settings.language) }}</h3>
            <p>{{ t("home.dealsDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Planning tile -->
        <router-link v-if="canViewPlanning" to="/planning" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/time.svg"
                :alt="t('home.planning', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.planning", settings.language) }}</h3>
            <p>{{ t("home.planningDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Timetracker tile -->
        <router-link
          v-if="canViewTimetracker"
          to="/timetracker"
          class="tool-card"
        >
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/time.svg"
                :alt="t('home.timetracker', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.timetracker", settings.language) }}</h3>
            <p>{{ t("home.timetrackerDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Tools tile -->
        <router-link v-if="canViewTools" to="/tools" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <img
                src="/assets/icons/tools.svg"
                :alt="t('home.tools', settings.language)"
                class="tool-icon-img"
              />
            </div>
            <h3>{{ t("home.tools", settings.language) }}</h3>
            <p>{{ t("home.toolsDesc", settings.language) }}</p>
          </div>
        </router-link>

        <!-- Chat tile -->
        <router-link v-if="canViewChat" to="/chat" class="tool-card">
          <div class="tool-content">
            <div class="tool-icon">
              <MessageSquareMore :stroke-width="1" />
            </div>
            <h3>{{ t("home.chat", settings.language) }}</h3>
            <p>{{ t("home.chatDesc", settings.language) }}</p>
          </div>
        </router-link>
      </div>

      <div class="recent-activity">
        <h2>{{ t("home.recentActivity", settings.language) }}</h2>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>{{ t("home.loading", settings.language) }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="error-icon">⚠️</div>
          <p>{{ error }}</p>
          <button @click="loadData" class="retry-btn">
            {{ t("home.tryAgain", settings.language) }}
          </button>
        </div>

        <!-- Activities List -->
        <div v-else class="activity-list">
          <div
            v-for="activity in recentActivities"
            :key="activity.id"
            class="activity-item"
          >
            <div class="activity-icon">
              <img
                :src="activity.icon"
                :alt="activity.type"
                class="activity-icon-img"
              />
            </div>
            <div class="activity-content">
              <p
                class="font-primary text-sm font-medium"
                v-html="activity.message"
              ></p>
              <span class="activity-time font-primary text-xs">{{
                activity.time
              }}</span>
            </div>
          </div>

          <!-- Empty State -->
          <div
            v-if="recentActivities.length === 0"
            class="empty-activity-state"
          >
            <div class="empty-icon">📊</div>
            <h3>{{ t("home.noRecentActivity", settings.language) }}</h3>
            <p>{{ t("home.addCompaniesToSeeActivity", settings.language) }}</p>
          </div>
        </div>
      </div>

      <!-- Add Task Popup -->
      <AddTaskPopup
        v-if="showAddTask"
        :visible="showAddTask"
        @close="showAddTask = false"
      />
      <!-- Add Call Popup -->
      <AddCallPopup
        v-if="showAddCall"
        :visible="showAddCall"
        @close="showAddCall = false"
      />
      <!-- Add Appointment Popup -->
      <AddAppointmentPopup
        v-if="showAddAppointment"
        :visible="showAddAppointment"
        @close="showAddAppointment = false"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from "../components/PageHeader.vue";
import Button from "@/components/Button.vue";
import ApiService from "@/services/api.js";
import { getRelativeTime } from "@/utils/helpers.js";
import AuthService from "@/services/auth.js";
import { MessageSquareMore } from "lucide-vue-next";
import { t } from "../utils/i18n";
import AddFabMenu from "../components/AddFabMenu.vue";
import AddTaskPopup from "../components/AddTaskPopup.vue";
import AddCallPopup from "../components/AddCallPopup.vue";
import AddAppointmentPopup from "../components/AddAppointmentPopup.vue";

export default {
  name: "Home",
  components: {
    PageHeader,
    Button,
    MessageSquareMore,
    AddFabMenu,
    AddTaskPopup,
    AddCallPopup,
    AddAppointmentPopup,
  },
  props: {
    sidebarVisible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      companies: [],
      recentActivities: [],
      loading: false,
      error: null,
      settings: {
        language: "nl",
      },
      t,
      showAddTask: false,
      showAddCall: false,
      showAddAppointment: false,
    };
  },
  mounted() {
    this.loadLanguage();
    this.loadData();
    window.addEventListener("storage", this.onStorageChange);
    window.addEventListener("language-changed", this.loadLanguage);
  },
  beforeDestroy() {
    window.removeEventListener("storage", this.onStorageChange);
    window.removeEventListener("language-changed", this.loadLanguage);
  },
  computed: {
    currentUser() {
      return AuthService.user;
    },
    userRights() {
      return (this.currentUser && this.currentUser.rights) || {};
    },
    isAdmin() {
      return (
        this.currentUser &&
        (this.currentUser.role === "admin" ||
          this.currentUser.role === "administrator")
      );
    },
    canViewAgenda() {
      return this.isAdmin || !!this.userRights.agenda;
    },
    canViewCompanies() {
      return (
        this.isAdmin ||
        !!this.userRights.companies ||
        !!this.userRights.crmCompanies
      );
    },
    canViewContacts() {
      return (
        this.isAdmin ||
        !!this.userRights.contacts ||
        !!this.userRights.crmContacts
      );
    },
    canViewProjects() {
      return (
        this.isAdmin ||
        !!this.userRights.projectsInvoices ||
        !!this.userRights.projectsExpenses ||
        !!this.userRights.projectsSubscriptions
      );
    },
    canViewPlanning() {
      return this.isAdmin || !!this.userRights.planning;
    },
    canViewDeals() {
      return this.isAdmin || !!this.userRights.dealsOffertes;
    },
    canViewOffertes() {
      return this.isAdmin || !!this.userRights.dealsOffertes;
    },
    canViewTimetracker() {
      return this.isAdmin || !!this.userRights.timeTracking;
    },
    canViewTools() {
      return this.isAdmin || !!this.userRights.settings;
    },
    canViewChat() {
      return this.isAdmin || !!this.userRights.settings;
    },
    canViewProspectie() {
      return this.isAdmin || !!this.userRights.prospectie;
    },
    canViewInfluencers() {
      return this.isAdmin || !!this.userRights.crmCompanies;
    },
    canViewArtists() {
      try {
        return AuthService.canViewArtists && AuthService.canViewArtists();
      } catch (err) {
        return this.isAdmin;
      }
    },
    canViewEmailTemplates() {
      return this.isAdmin || !!this.userRights.crmMailing;
    },
    canViewArticles() {
      return this.isAdmin || !!this.userRights.articles;
    },
  },
  methods: {
    onAddFabMenu(type) {
      if (type === "task") this.showAddTask = true;
      else if (type === "call") this.showAddCall = true;
      else if (type === "appointment") this.showAddAppointment = true;
      else if (type === "project") this.$router.push({ path: "/add-project" });
      else if (type === "deal") this.$router.push({ path: "/deal-add" });
    },
    loadLanguage() {
      const savedSettings = localStorage.getItem("userSettings");
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        if (parsed.language) {
          this.settings.language = parsed.language;
        }
      }
    },
    onStorageChange(e) {
      if (e.key === "userSettings") {
        this.loadLanguage();
      }
    },
    async loadData() {
      this.loading = true;
      this.error = null;

      try {
        // Fetch companies from API
        this.companies = await ApiService.getCompanies();

        // Generate recent activities based on actual company data
        this.generateRecentActivities();
      } catch (error) {
        console.error("Error loading data:", error);
        this.error = "An error occurred while loading the data.";

        // Fallback to mock data if API fails
        this.generateMockActivities();
      } finally {
        this.loading = false;
      }
    },

    generateRecentActivities() {
      const activities = [];
      const now = new Date();

      // Sort companies by creation date (assuming newer IDs are more recent)
      const sortedCompanies = [...this.companies].sort((a, b) => b.id - a.id);

      // Add recent company additions
      sortedCompanies.slice(0, 2).forEach((company, index) => {
        const hoursAgo = (index + 1) * 2; // 2, 4 hours ago
        activities.push({
          id: `add-${company.id}`,
          type: "add",
          icon: "/assets/icons/add.svg",
          message: `<strong>New company added:</strong> ${company.name}`,
          time: `${hoursAgo} hours ago`,
          timestamp: new Date(now.getTime() - hoursAgo * 60 * 60 * 1000),
        });
      });

      // Add recent company updates (mock based on existing companies)
      if (this.companies.length > 2) {
        const companyToUpdate =
          this.companies[
            Math.floor(Math.random() * Math.min(3, this.companies.length))
          ];
        activities.push({
          id: `edit-${companyToUpdate.id}`,
          type: "edit",
          icon: "/assets/icons/edit_square.svg",
          message: `<strong>Company updated:</strong> ${companyToUpdate.name} - Contact information`,
          time: "4 hours ago",
          timestamp: new Date(now.getTime() - 4 * 60 * 60 * 1000),
        });
      }

      // Add search activity
      activities.push({
        id: "search-1",
        type: "search",
        icon: "/assets/icons/search.svg",
        message: '<strong>Search performed:</strong> "IT companies Antwerp"',
        time: "6 hours ago",
        timestamp: new Date(now.getTime() - 6 * 60 * 60 * 1000),
      });

      // Sort by timestamp (most recent first)
      this.recentActivities = activities.sort(
        (a, b) => b.timestamp - a.timestamp,
      );
    },

    generateMockActivities() {
      // Fallback mock data when API is not available
      this.recentActivities = [
        {
          id: "mock-1",
          type: "add",
          icon: "/assets/icons/add.svg",
          message: "<strong>New company added:</strong> TechCorp Belgium",
          time: "2 hours ago",
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        },
        {
          id: "mock-2",
          type: "edit",
          icon: "/assets/icons/edit_square.svg",
          message:
            "<strong>Company updated:</strong> InnovateLab - Contact information",
          time: "4 hours ago",
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        },
        {
          id: "mock-3",
          type: "search",
          icon: "/assets/icons/search.svg",
          message: '<strong>Search performed:</strong> "IT companies Antwerp"',
          time: "6 hours ago",
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
        },
      ];
    },
  },
};
</script>

<style scoped>
.home {
  min-height: 100vh;
  background: var(--background);
}

.home-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.page-header h1 {
  font-size: 2rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-weight: 700;
}

.page-header p {
  color: var(--text-secondary);
}

.company-count {
  color: var(--primary_color);
  font-weight: 600;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.tool-card {
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--border);
  transition: all 0.3s ease;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  display: block;
}

.tool-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border-color: var(--primary_color);
}

.tool-card:hover .tool-icon {
  background: var(--primary_color);
}

.tool-card:hover .tool-icon-img {
  filter: brightness(0) invert(0);
}

.tool-card.active {
  border-color: var(--primary_color);
  box-shadow: 0 4px 20px var(--primary_color_alpha_15);
}

.tool-content {
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tool-card h3 {
  color: var(--text-primary) !important;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
  font-weight: 600;
}

.tool-card p {
  color: var(--text-secondary) !important;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
}

.tool-icon {
  width: 48px;
  height: 48px;
  background: var(--primary_color);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 1rem;
}

.tool-icon-img {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(0); /* Make icon black by default */
}

/* Light mode: white icons on active cards */
[data-theme="light"] .tool-card.active .tool-icon-img {
  filter: brightness(0) invert(1); /* Make icon white in light mode when active */
}

/* Dark mode: keep icons white on active cards */
[data-theme="dark"] .tool-card.active .tool-icon-img {
  filter: brightness(0) invert(1); /* Make icon white in dark mode when active */
}

.tool-card:not(.active) .tool-icon {
  background: var(--border);
  color: var(--text-secondary);
}

.tool-card:not(.active) .tool-icon-img {
  filter: brightness(0) invert(0.45);
}

.tool-card:hover:not(.active) .tool-icon {
  background: var(--primary_color);
}

.tool-card:hover:not(.active) .tool-icon-img {
  filter: brightness(0) invert(0);
}

.tool-status {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tool-status.active {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.tool-status.coming-soon {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.recent-activity {
  background: var(--surface);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--border);
}

.recent-activity h2 {
  color: var(--text-primary);
  margin-bottom: 1.5rem;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: var(--background);
  border: 1px solid var(--border);
}

.activity-icon {
  width: 36px;
  height: 36px;
  background: var(--primary_color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.activity-icon-img {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1); /* Make icon white */
}

.activity-content {
  flex: 1;
}

.activity-content p {
  color: var(--text-primary) !important;
  margin-bottom: 0.25rem;
}

.activity-time {
  color: var(--text-secondary) !important;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border);
  border-top: 4px solid var(--primary_color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: var(--text-secondary);
  margin: 0;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.error-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.error-state p {
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

.retry-btn {
  background: var(--primary_color);
  color: black;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: var(--primary_color);
  transform: translateY(-1px);
}

/* Empty Activity State */
.empty-activity-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.empty-activity-state h3 {
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
}

.empty-activity-state p {
  color: var(--text-secondary);
  margin: 0;
}

/* Tablet and up */
@media (min-width: 768px) {
  .page-header h1 {
    font-size: 2.5rem;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .home-container {
    padding: 0;
  }

  .page-header h1 {
    font-size: 2rem;
  }

  .tools-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .tool-content {
    padding: 1.25rem;
  }

  .recent-activity {
    padding: 1.25rem;
  }

  .activity-item {
    padding: 0.75rem;
  }
}

/* Desktop and up */
@media (min-width: 1024px) {
  .page-header h1 {
    font-size: 3rem;
  }
}
</style>
